<template>
    <div class="page-container">
        <!-- Tab 切换栏 -->
        <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabClick" class="tabs-container">
            <el-tab-pane label="违规行为列表" name="list">
                <!-- 列表筛选栏 -->
                <el-form inline :model="searchForm" class="search-form">
                    <el-form-item label="开始时间">
                        <el-date-picker v-model="searchForm.startTime" type="datetime" placeholder="选择开始时间"
                            value-format="yyyy-MM-dd HH:mm:ss" :disabled-date="disabledDate" />
                    </el-form-item>
                    <el-form-item label="-">
                        <el-date-picker v-model="searchForm.endTime" type="datetime" placeholder="选择结束时间"
                            value-format="yyyy-MM-dd HH:mm:ss" :disabled-date="disabledDate" />
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
                        <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
                    </el-form-item>
                </el-form>

                <!-- 列表表格 -->
                <el-table :data="pagedTableData" border style="width: 100%; margin-top: 20px;" height="62vh"
                    class="table-container" v-loading="tableLoading" element-loading-text="数据加载中...">
                    <el-table-column prop="index" label="序号" width="80" align="center" />
                    <el-table-column prop="chid" label="通道编号" align="center" width="80" />
                    <el-table-column prop="ip" label="摄像头IP" align="center" min-width="120" />
                    <el-table-column prop="sn" label="设备序列号" align="center" min-width="180" />
                    <el-table-column prop="desc" label="违规项" align="center" min-width="150" />
                    <el-table-column prop="location" label="设备位置" align="center" min-width="120" />
                    <el-table-column prop="pic_data" label="违规图片" align="center" width="100">
                        <template slot-scope="scope">
                            <el-image v-if="scope.row.pic_data" :src="scope.row.pic_data"
                                style="width: 80px; height: 60px; object-fit: cover; cursor: pointer;" fit="cover"
                                :preview-src-list="[scope.row.pic_data]" preview-teleport="body" />
                            <span v-else class="no-image">无</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="违规时间" align="center" min-width="180">
                        <template slot-scope="scope">
                            {{ formatTimestamp(scope.row.timestamp) }}
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 列表分页组件 -->
                <div class="list-pagination-container">
                    <el-pagination @size-change="handleListSizeChange" @current-change="handleListCurrentChange"
                        :current-page="listCurrentPage" :page-sizes="[10, 20, 50, 100]" :page-size="listPageSize"
                        layout="total, sizes, prev, pager, next, jumper" :total="totalTableCount" background>
                    </el-pagination>
                </div>
            </el-tab-pane>

            <el-tab-pane label="健康证图片管理" name="healthCard">
                <!-- 健康证图片管理区域 -->
                <div class="health-card-container">
                    <!-- 头部：标题+说明+统计 -->
                    <div class="health-header">
                        <h3 class="header-title">健康证图片管理</h3>
                        <p class="header-desc">支持JPG/PNG格式图片上传，单张不超过200KB，最多可上传10张</p>
                        <div class="header-count">共 {{ healthCardImages.length }} 张图片</div>
                    </div>

                    <!-- 上传区域：独立一行，不参与分页 -->
                    <div class="upload-wrapper">
                        <!-- 关键修复1：添加 action="#" 属性，解决必填项报错 -->
                        <!-- 关键修复2：优化 disabled 条件，结合上传中状态避免重复上传 -->
                        <el-upload class="health-upload" action="#" :http-request="handleHealthCardUpload"
                            list-type="picture-card" :on-preview="handlePicturePreview"
                            :on-remove="handleHealthCardRemove" :limit="maxUploadCount" :file-list="[]"
                            :auto-upload="true" :before-upload="beforeUpload" accept=".jpg,.png,.jpeg"
                            :disabled="healthCardImages.length >= maxUploadCount || isUploading"
                            v-loading="uploadLoading">
                            <i class="el-icon-plus" />
                            <div slot="tip" class="el-upload__tip" v-if="healthCardImages.length < maxUploadCount">
                                单张不超过200KB，最多上传10张
                            </div>
                            <div slot="tip" class="el-upload__tip upload-disabled" v-else>
                                已达到最大上传数量（10张）
                            </div>
                        </el-upload>
                    </div>

                    <!-- 图片列表：分页展示（一页6张） -->
                    <div class="health-list-container" v-if="healthCardImages.length > 0">
                        <div class="health-grid">
                            <div class="health-image-item" v-for="(img, index) in currentPageImages"
                                :key="img.id || img.uid" @click="handlePicturePreview(img)">
                                <el-image :src="img.picUrl || img.url" :alt="`健康证${index + 1}`" class="card-image"
                                    :preview-src-list="[img.picUrl || img.url]" preview-teleport="body" />
                                <div class="image-actions">
                                    <el-button icon="el-icon-delete" size="mini" type="text"
                                        @click.stop="handleHealthCardRemove(img)" class="delete-btn" />
                                </div>
                            </div>
                        </div>

                        <!-- 分页组件 -->
                        <div class="pagination-container">
                            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                                :current-page="currentPage" :page-sizes="[6]" :page-size="pageSize"
                                layout="total, prev, pager, next, jumper" :total="healthCardImages.length" background />
                        </div>
                    </div>

                    <!-- 空状态提示（无图片时显示） -->
                    <div class="health-empty" v-if="healthCardImages.length === 0 && !uploadLoading">
                        <div class="empty-icon">
                            <i class="el-icon-picture-outline" />
                        </div>
                        <div class="empty-text">暂无健康证图片</div>
                        <div class="empty-desc">点击上传按钮添加健康证图片</div>
                    </div>
                </div>

                <!-- 预览弹窗 -->
                <el-dialog :visible.sync="dialogVisible" title="图片预览" width="90%" max-width="1200px" append-to-body
                    destroy-on-close class="preview-dialog">
                    <div class="preview-content">
                        <img :src="dialogImageUrl" alt="图片预览" class="preview-img" v-loading="previewLoading" />
                    </div>
                    <div slot="footer" class="preview-footer">
                        <el-button @click="dialogVisible = false">关闭</el-button>
                        <el-button type="primary" @click="downloadPreviewImg">下载图片</el-button>
                    </div>
                </el-dialog>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script>
import { getBehaviorList } from "@/api/xingwei";
import { addCarousel, getCarouselList, delCarousel } from "@/api/promotion";

export default {
    name: "ViolationAndHealthCard",
    data() {
        return {
            // 基础配置
            activeTab: 'list', // 默认激活列表Tab
            maxUploadCount: 10, // 健康证最大上传数量
            userId: localStorage.getItem('userId') || '', // 用户ID

            // 列表搜索条件
            searchForm: {
                startTime: '',
                endTime: ''
            },

            // 列表分页相关
            rawTableData: [], // 原始列表数据
            listCurrentPage: 1, // 列表当前页码
            listPageSize: 10,   // 列表每页展示数量
            totalTableCount: 0,  // 列表总条数
            tableLoading: false, // 列表加载状态

            // 健康证图片相关
            healthCardImages: [], // 健康证图片列表（接口数据）
            currentPage: 1, // 健康证当前页码
            pageSize: 6,    // 健康证每页展示数量
            dialogVisible: false, // 预览弹窗显隐
            dialogImageUrl: '', // 预览图片地址
            uploadLoading: false, // 上传加载状态
            previewLoading: false, // 预览加载状态
            isUploading: false // 上传中标记
        }
    },
    computed: {
        // 健康证当前页展示的图片
        currentPageImages() {
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            return this.healthCardImages.slice(startIndex, endIndex);
        },
        // 列表当前页展示的数据（带序号）
        pagedTableData() {
            const startIndex = (this.listCurrentPage - 1) * this.listPageSize;
            const endIndex = startIndex + this.listPageSize;
            return this.rawTableData.slice(startIndex, endIndex).map((item, index) => ({
                ...item,
                index: startIndex + index + 1
            }));
        }
    },
    created() {
        // 初始化校验用户ID
        if (!this.userId) {
            this.$message.warning("用户未登录，请重新登录后操作");
            return;
        }
        // 加载数据
        this.loadTableData();
        this.loadHealthCardImages();
    },
    methods: {
        // ========== 通用工具方法 ==========
        /**
         * 时间戳格式化
         * @param {Number|String} timestamp - 时间戳（秒/毫秒）
         * @returns {String} 格式化后的时间
         */
        formatTimestamp(timestamp) {
            if (!timestamp || isNaN(Number(timestamp))) {
                return '暂无时间';
            }

            // 统一转换为毫秒级
            let msTimestamp = Number(timestamp);
            if (msTimestamp.toString().length === 10) {
                msTimestamp *= 1000;
            }

            const date = new Date(msTimestamp);
            if (date.toString() === 'Invalid Date') {
                return '时间格式错误';
            }

            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
        },

        /**
         * 禁用未来日期选择
         * @param {Date} date - 日期对象
         * @returns {Boolean} 是否禁用
         */
        disabledDate(date) {
            return date > new Date();
        },

        // ========== 违规列表相关 ==========
        /**
         * 加载违规行为列表数据
         */
        async loadTableData() {
            if (!this.userId) return;

            this.tableLoading = true;
            try {
                const response = await getBehaviorList({ userId: this.userId });
                let apiData = response.data || [];

                // 接口返回空时填充仿真数据
                if (apiData.length === 0) {
                    apiData = [];
                }

                this.rawTableData = apiData;
                this.totalTableCount = this.rawTableData.length;
            } catch (error) {
                console.error('加载违规列表失败：', error);
                // 接口失败时填充仿真数据
                this.rawTableData = [];
                this.totalTableCount = this.rawTableData.length;
                this.$message.error('数据加载失败，已显示测试数据');
            } finally {
                this.tableLoading = false;
            }
        },

        /**
         * 列表搜索
         */
        handleSearch() {
            if (!this.searchForm.startTime && !this.searchForm.endTime) {
                this.$message.info("请选择搜索时间范围");
                return;
            }

            this.listCurrentPage = 1; // 重置分页
            const startTimeStamp = this.searchForm.startTime ? new Date(this.searchForm.startTime).getTime() : 0;
            const endTimeStamp = this.searchForm.endTime ? new Date(this.searchForm.endTime).getTime() : Date.now();

            // 筛选时间范围内的数据
            this.rawTableData = this.rawTableData.filter(item => {
                let itemTime = Number(item.timestamp);
                if (itemTime.toString().length === 10) itemTime *= 1000; // 秒转毫秒
                return itemTime >= startTimeStamp && itemTime <= endTimeStamp;
            });
            this.totalTableCount = this.rawTableData.length;
        },

        /**
         * 重置搜索条件
         */
        handleReset() {
            this.searchForm = { startTime: '', endTime: '' };
            this.listCurrentPage = 1;
            this.loadTableData(); // 重新加载全部数据
        },

        /**
         * 列表分页大小改变
         * @param {Number} val - 每页数量
         */
        handleListSizeChange(val) {
            this.listPageSize = val;
            this.listCurrentPage = 1;
        },

        /**
         * 列表页码改变
         * @param {Number} val - 当前页码
         */
        handleListCurrentChange(val) {
            this.listCurrentPage = val;
        },

        // ========== 健康证图片相关 ==========
        /**
         * 加载健康证图片列表
         */
        async loadHealthCardImages() {
            if (!this.userId) return;

            try {
                const res = await getCarouselList({
                    userId: this.userId,
                    picType: "健康证",
                });

                // 格式化接口数据
                if (res.data && Array.isArray(res.data)) {
                    this.healthCardImages = res.data.filter(item => item.picUrl).map(item => ({
                        id: item.id,
                        uid: item.id || `health-${Date.now()}`,
                        picUrl: item.picUrl,
                        picName: item.picName || '健康证图片',
                        createTime: item.createTime || new Date().toLocaleString()
                    }));
                } else {
                    this.healthCardImages = [];
                }
            } catch (error) {
                console.error("加载健康证图片失败：", error);
                this.$message.error("加载健康证图片失败！");
                this.healthCardImages = [];
            }
        },

        /**
         * 健康证图片自定义上传
         * @param {Object} options - 上传选项
         */
        async handleHealthCardUpload(options) {
            // 关键修复3：添加上传中标记，避免重复上传
            if (this.isUploading) {
                this.$message.warning("正在上传中，请稍候");
                options.onError(new Error("重复上传"));
                return;
            }

            const file = options.file;
            this.uploadLoading = true;
            this.isUploading = true; // 标记上传中

            try {
                // 校验
                if (!this.userId) {
                    throw new Error("用户未登录");
                }

                // 构建上传参数
                const formData = new FormData();
                formData.append("file", file);
                formData.append("picName", file.name || "健康证图片");
                formData.append("picType", "健康证");
                formData.append("sort", this.healthCardImages.length + 1);
                formData.append("userId", this.userId);

                // 调用上传接口
                const res = await addCarousel(formData);

                // 更新图片列表
                const newImage = {
                    id: res.data.id,
                    uid: res.data.id,
                    picUrl: res.data.picUrl,
                    picName: file.name,
                    createTime: new Date().toLocaleString()
                };
                this.healthCardImages.unshift(newImage); // 最新的放前面

                options.onSuccess(res.data);
                this.$message.success("健康证图片上传成功！");
                this.loadHealthCardImages()
                // 重置分页
                if (this.healthCardImages.length <= this.pageSize) {
                    this.currentPage = 1;
                }
            } catch (error) {
                console.error("健康证上传失败：", error);
                this.$message.error(`上传失败：${error.message || '网络错误'}`);
                options.onError(error);
            } finally {
                this.uploadLoading = false;
                this.isUploading = false; // 取消上传中标记
            }
        },

        /**
         * 删除健康证图片
         * @param {Object} img - 图片对象
         */
        async handleHealthCardRemove(img) {
            try {
                // 校验参数
                if (!img || (!img.id && !img.uid)) {
                    this.$message.warning("无效的图片数据");
                    return;
                }

                // // 二次确认
                // await this.$confirm("确定删除这张健康证图片吗？", "删除确认", {
                //     type: "warning",
                //     confirmButtonText: "确定",
                //     cancelButtonText: "取消"
                // });

                // 调用删除接口
                await delCarousel({
                    id: img.id || img.uid,
                    userId: this.userId
                });

                // 更新列表
                this.healthCardImages = this.healthCardImages.filter(item =>
                    item.id !== img.id && item.uid !== img.uid
                );
                this.$message.success("图片已删除！");

                // 分页调整
                if (this.currentPageImages.length === 0 && this.currentPage > 1) {
                    this.currentPage--;
                }
            } catch (error) {
                if (error !== "cancel") { // 排除取消操作
                    console.error("删除图片失败：", error);
                    this.$message.error("删除失败！");
                }
            }
        },

        /**
         * 上传前校验
         * @param {File} file - 上传文件
         * @returns {Boolean} 是否允许上传
         */
        beforeUpload(file) {
            const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
            const isLt200K = file.size / 1024 < 200; // 200KB限制

            if (!isImage) {
                this.$message.error('仅支持上传JPG/PNG格式图片');
                return false;
            }
            if (!isLt200K) {
                this.$message.error('图片大小不能超过200KB');
                return false;
            }
            return true;
        },

        /**
         * 图片预览
         * @param {Object} file - 图片对象
         */
        handlePicturePreview(file) {
            this.previewLoading = true;
            this.dialogImageUrl = file.picUrl || file.url || file.response?.picUrl || file.objectURL;
            this.dialogVisible = true;

            // 模拟加载完成
            setTimeout(() => {
                this.previewLoading = false;
            }, 500);
        },

        /**
         * 下载预览图片
         */
        downloadPreviewImg() {
            if (!this.dialogImageUrl) {
                this.$message.warning("暂无可下载的图片");
                return;
            }

            try {
                const link = document.createElement('a');
                link.href = this.dialogImageUrl;
                link.download = `健康证_${new Date().getTime()}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.$message.success('图片下载中，请稍候');
            } catch (error) {
                console.error("下载图片失败：", error);
                this.$message.error("图片下载失败，请手动保存");
            }
        },

        // ========== 其他事件处理 ==========
        /**
         * Tab切换事件
         * @param {Object} tab - Tab对象
         */
        handleTabClick(tab) {
            // 切换到健康证Tab时刷新数据
            if (tab.name === 'healthCard') {
                this.loadHealthCardImages();
            }
        },

        /**
         * 健康证分页大小改变
         * @param {Number} val - 每页数量
         */
        handleSizeChange(val) {
            this.pageSize = val;
            this.currentPage = 1;
        },

        /**
         * 健康证页码改变
         * @param {Number} val - 当前页码
         */
        handleCurrentChange(val) {
            this.currentPage = val;
        }
    },
    // 关键修复4：组件销毁时清理资源，避免内存泄漏
    beforeDestroy() {
        this.dialogVisible = false;
        this.dialogImageUrl = '';
        this.isUploading = false;
        this.uploadLoading = false;
    }
}
</script>

<style lang="scss" scoped>
// 全局容器样式
.page-container {
    padding: 24px;
    background-color: #f9f9f9;
    min-height: 100vh;

    // Tab容器样式
    .tabs-container {
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        overflow: hidden;

        ::v-deep .el-tabs__header {
            padding: 0 20px;
        }
    }

    // 搜索表单样式
    .search-form {
        padding: 0 20px;
        margin-bottom: 20px;
        padding-top: 20px;

        ::v-deep .el-form-item {
            margin-bottom: 15px;
        }
    }

    // 表格容器样式
    .table-container {
        margin: 0 20px;

        ::v-deep .el-table__empty-text {
            color: #999;
            font-size: 14px;
        }

        .no-image {
            color: #999;
            font-size: 12px;
        }
    }

    // 列表分页容器样式
    .list-pagination-container {
        display: flex;
        justify-content: center;
        margin: 20px;
        padding-top: 10px;
        border-top: 1px solid #f0f0f0;
    }

    // 列表图片hover效果
    ::v-deep .el-table .el-image {
        transition: all 0.3s;

        &:hover {
            transform: scale(1.08);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            border-radius: 4px;
        }
    }

    // 健康证图片管理区域核心样式
    .health-card-container {
        padding: 24px;
        background-color: #fff;
        border-radius: 8px;

        // 头部样式
        .health-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            flex-wrap: wrap;
            gap: 10px;

            .header-title {
                font-size: 16px;
                font-weight: 500;
                color: #333;
                margin: 0;
            }

            .header-desc {
                font-size: 12px;
                color: #999;
                margin: 0;
                flex: 1;
                min-width: 200px;
            }

            .header-count {
                font-size: 12px;
                color: #666;
                background-color: #f5f7fa;
                padding: 4px 12px;
                border-radius: 16px;
            }
        }

        // 上传按钮容器
        .upload-wrapper {
            margin-bottom: 20px;

            ::v-deep .health-upload {
                .el-upload--picture-card {
                    width: 120px;
                    height: 120px;
                    border: 1px dashed #e5e6eb;
                    border-radius: 4px;
                    background-color: #fafafa;
                    transition: all 0.3s;

                    &:hover {
                        border-color: #409eff;
                        background-color: #f0f7ff;
                    }

                    .el-icon-plus {
                        font-size: 24px;
                        color: #409eff;
                    }
                }

                .upload-disabled {
                    color: #999;
                    background-color: #f5f5f5;
                    padding: 4px 0;
                }
            }
        }

        // 图片列表容器
        .health-list-container {

            // 网格布局：每行6张（自适应）
            .health-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                gap: 16px;
                margin-bottom: 24px;
            }

            // 单个图片项样式
            .health-image-item {
                width: 100%;
                border-radius: 6px;
                overflow: hidden;
                background-color: #fff;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
                transition: all 0.3s;
                cursor: pointer;
                position: relative;

                &:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                // 图片样式（确保图片填充容器）
                ::v-deep .card-image {
                    width: 100% !important;
                    height: 160px !important;

                    .el-image__inner {
                        width: 100% !important;
                        height: 160px !important;
                        // object-fit: cover;
                    }
                }

                // 图片操作区（删除按钮）
                .image-actions {
                    display: flex;
                    justify-content: flex-end;
                    padding: 8px 10px;
                    background-color: #fafafa;
                    border-top: 1px solid #f0f0f0;

                    .delete-btn {
                        color: #f56c6c;
                        padding: 0;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        &:hover {
                            background-color: rgba(245, 108, 108, 0.1);
                        }
                    }
                }
            }

            // 分页容器
            .pagination-container {
                display: flex;
                justify-content: center;
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #f0f0f0;
            }
        }

        // 空状态样式
        .health-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 0;
            color: #999;

            .empty-icon {
                font-size: 48px;
                margin-bottom: 16px;
                color: #c0c4cc;
            }

            .empty-text {
                font-size: 16px;
                margin-bottom: 8px;
            }

            .empty-desc {
                font-size: 13px;
            }
        }

        // 预览弹窗样式
        .preview-dialog {
            ::v-deep .el-dialog__body {
                padding: 20px;
                max-height: 80vh;
                overflow: hidden;
            }

            .preview-content {
                text-align: center;
                padding: 10px 0;
            }

            .preview-img {
                max-width: 100%;
                max-height: 70vh;
                border-radius: 6px;
                box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
            }

            .preview-footer {
                text-align: right;
            }
        }
    }
}
</style>